import { Alert, Box, Button, Chip, Paper, TextField } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { EntryField, EntryTitle, EntryWrapper, FormWrapper, } from "../../../components/customStyles/form";
import UserFormFields, { updateUserState, } from "../../../components/input/form/UserFormFields";
import PasswordResetDialog from "../../../components/modal/PasswordResetDialog";
import EditOnly from "../../../components/permissions/EditOnly";
import UserStatusDialog from "../../../components/modal/UserStatusDialog";
import DataTable from "../../../components/table/data/DataTable";
import { useRawScansColumns, useUserColumns, useUserNotificationsColumns, } from "../../../hooks/useTableColumns";
import { ELanguage } from "../../../models/enums/language";
import { ERole } from "../../../models/enums/role";
import { IUserGroup } from "../../../models/propertyGroup";
import { IScanWithId } from "../../../models/stat";
import IUser from "../../../models/user";
import IUserNotificationDetail from "../../../models/userNotificationDetail";
import propertyGroupService from "../../../services/propertyGroupService/PropertyGroupService";
import hbscanStatService from "../../../services/stat/HBScanStatService";
import UserService from "../../../services/user/UserService";
import userNotificationService from "../../../services/userNotification/UserNotificationService";
import { theme } from "../../../styles/themes/mainTheme";
import { validEmailRegex, validUserCodeRegex } from "../../../utils/regex";
import { IUserState } from "./UserAddPage";
import { IUserDetails } from "./UserPage";
import WorkSchedulesSection, { normalizeToPayload } from "../../../components/input/schedules/WorkSchedulesSection";
import { IWorkSchedule } from "../../../models/workSchedule";
import WorkScheduleService from "../../../services/workSchedule/WorkScheduleService";
import { Save } from "@mui/icons-material";


export interface IUpdatedUser {
    id: number;
    code?: string;
    isManualCode?: boolean;
    name: string;
    firstName?: string;
    lastName?: string;
    email: string;
    role: ERole;
    language: ELanguage;
    managerId?: number;
    isFormSending?: boolean;
    createDate?: Date;
    isLocked: boolean;
    buildingIds?: number[];
    propertyGroupIds?: number[];
    microsoftId?: string;
    acombaEmployeeId?: number;
    acombaFunctionNumber?: number;
}

const initialUserState: IUpdatedUser = {
    id: 0,
    code: "",
    isManualCode: false,
    name: "",
    firstName: "",
    lastName: "",
    email: "",
    role: ERole.Employee,
    managerId: 0,
    language: ELanguage.English,
    isFormSending: false,
    createDate: new Date(),
    isLocked: false,
    buildingIds: [],
    propertyGroupIds: [],
    microsoftId: undefined,
    acombaEmployeeId: undefined,
    acombaFunctionNumber: undefined,
};

export const loadUsersWithLastSessionDates = async (
    users: IUser[]
): Promise<IUserDetails[]> => {
    if (!users.length) return Promise.resolve([]);

    const userIds = users.map((user) => user.id);

    const lastSessions = await UserService.GetLastSessionsByUserIds(userIds);
    if (!lastSessions) return [];
    return users.map((user) => {
        const lastSessionDate = lastSessions.find(
            (lastSession) => lastSession.userId === user.id
        )?.createDate;

        const [firstName, lastName] = user.name.trim().split(" ");

        return {
            ...user,
            firstName,
            lastName,
            lastSessionDate,
        };
    });
};

const SUCCESS_STATUS_CODE = 200;
const SUCCESS_MESSAGE_TIMEOUT = 3000;
const ERROR_MESSAGE_TIMEOUT = 5000;
const ROWS_PER_PAGE = 10;

const UserEditPage = () => {
    const usersColumns = useUserColumns();
    const scansColumns = useRawScansColumns({ hasUserName: false });
    const userNotificationsColumns = useUserNotificationsColumns({ hasLocalName: true, hasUserName: false });

    const { t } = useTranslation();
    const [areScansLoading, setAreScansLoading] = useState(true);
    const [areAlertsLoading, setAreAlertsLoading] = useState(true);

    const [isStatusConfirmationOpen, setIsStatusConfirmationOpen] = useState(false);

    const [isPwdConfirmationOpen, setIsPwdConfirmationOpen] = useState(false);
    const [isPwdResetSuccessVisible, setIsPwdResetSuccessVisible] =
        useState(false);
    const [isPwdResetErrorVisible, setIsPwdResetErrorVisible] = useState(false);

    const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(false);

    const [managers, setManagers] = useState<IUser[]>([]);
    const [managersUsers, setManagersUsers] = useState<IUserDetails[]>([]);

    const [userState, setUserState] = useState<IUpdatedUser>(initialUserState);
    const [cachedUser, setCachedUser] =
        useState<IUpdatedUser>(initialUserState);
    const [propertyGroups, setPropertyGroups] = useState<IUserGroup[]>([]);

    const [isUserChanged, setIsUserChanged] = useState(false);

    const [userScans, setUserScans] = useState<IScanWithId[]>([]);
    const [userNotifications, setUserNotifications] = useState<
        IUserNotificationDetail[]
    >([]);

    const [workSchedules, setWorkSchedules] = useState<IWorkSchedule[]>([]);
    const [initialSchedules, setInitialSchedules] = useState<IWorkSchedule[] | null>(null);
    const [isSchedulesChanged, setIsSchedulesChanged] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { id: userId } = location.state;

    const isValidForm = () => {
        if (!validUserCodeRegex.test(userState.code || ""))
            return false;

        if (
            !userState.firstName?.trim() ||
            !userState.lastName?.trim() ||
            !userState.role ||
            !userState.language
        )
            return false;

        if (
            (userState.role === ERole.Admin || userState.role === ERole.Manager || userState.role === ERole.Supervisor || userState.role === ERole.Client || userState.role === ERole.LimitedClient) &&
            !validEmailRegex.test(userState.email!)
        )
            return false;
        if (
            userState.role === ERole.Employee &&
            !managers.find((manager) => manager.id === userState.managerId)
        )
            return false;

        if (
            workSchedules.length !== 0 &&
            workSchedules.some(s =>
                s.days.some(
                    d =>
                        !d.isHoliday &&
                        (
                            !d.startTime ||
                            !d.endTime ||
                            d.startTime === d.endTime ||
                            (d.breakMinutes === undefined || d.breakMinutes === null || d.breakMinutes < 0)
                        )
                )
            )
        ) {
            return false;
        }

        return true;
    };

    const onUpdateUser = async () => {
        setUserState((prevState) => ({
            ...prevState,
            isFormSending: true,
            acombaEmployeeId: prevState.acombaEmployeeId || undefined,
            acombaFunctionNumber: prevState.acombaFunctionNumber || undefined,
        }));

        try {
            const user = await UserService.PutUser(userState)
            if (!user) return setIsErrorMessageVisible(true);

            await saveSchedules(user.id);
            navigate(0);
        } catch (error) {
            setIsErrorMessageVisible(true);
        } finally {
            setUserState((prevState) => ({
                ...prevState,
                isFormSending: false,
            }));
        }
    };

    const saveSchedules = async (userId: number) => {
        if (!userId) return;

        await WorkScheduleService.UpsertMany(userId, workSchedules.map(schedule => normalizeToPayload({ ...schedule, userId })));
    };

    const normalizeSchedules = (arr: IWorkSchedule[]) =>
        [...arr]
            .map(s => ({
                ...s,
                days: [...s.days].sort((a, b) => Number(a.weekday) - Number(b.weekday)),
            }))
            .sort((a, b) => a.propertyGroupId - b.propertyGroupId);

    useEffect(() => {
        const a = JSON.stringify(normalizeSchedules(workSchedules));
        const b = initialSchedules
            ? JSON.stringify(normalizeSchedules(initialSchedules))
            : JSON.stringify([]);
        setIsSchedulesChanged(a !== b);
    }, [workSchedules, initialSchedules]);

    const onToggleUserStatus = () => {
        UserService.PutUser({ ...cachedUser, isLocked: !userState.isLocked })
            .then((response) => {
                if (response) {
                    navigate(0);
                    return;
                }
                setIsErrorMessageVisible(true);
            })
    };

    const onResetPassword = () => {
        UserService.PutResetPassword(cachedUser).then((response) => {
            if (response.status === SUCCESS_STATUS_CODE) {
                setIsPwdResetErrorVisible(false);
                setIsPwdResetSuccessVisible(true);
                setTimeout(() => {
                    setIsPwdConfirmationOpen(false);
                    setIsPwdResetSuccessVisible(false);
                }, SUCCESS_MESSAGE_TIMEOUT);
                return;
            }
            setIsPwdResetErrorVisible(true);
            setTimeout(() => {
                setIsPwdResetErrorVisible(false);
            }, ERROR_MESSAGE_TIMEOUT);
        });
    };

    useEffect(() => {
        UserService.GetUserById(Number(userId)).then((user) => {
            propertyGroupService.GetUserGroupsByUserId().then((userGroups) => {
                setPropertyGroups(userGroups);

                if (!user) return;
                const name = user.name.trim();
                const [firstName, lastName] = name.split(" ");
                const userDetails = {
                    ...user,
                    code: user.code,
                    isManualCode: user.isManualCode,
                    name,
                    firstName,
                    lastName,
                    isFormSending: false,
                    managerId: user.managerId,
                    createDate: user.createDate,
                    isLocked: user.isLocked,
                    buildingIds: user.buildingIds && user.buildingIds[0] !== 0 ? user.buildingIds : [],
                    microsoftId: user.microsoftId,
                    propertyGroupIds: user.propertyGroupIds,
                    acombaEmployeeId: user.acombaEmployeeId,
                    acombaFunctionNumber: user.acombaFunctionNumber,
                };
                setUserState(userDetails);
                setCachedUser(userDetails);

                if (user.role !== ERole.Admin) return;
            });

            UserService.GetUsersByManager(user.id).then((users) => {
                if (!user) return;
                loadUsersWithLastSessionDates(users).then(
                    (usersWithLastSessionDates) => {
                        if (usersWithLastSessionDates)
                            setManagersUsers(usersWithLastSessionDates);
                    }
                );
            });
        });

        hbscanStatService.getRawScansByUser(Number(userId)).then((scans) => {
            if (!scans) return;

            scans.sort((a, b) => moment(b.date).unix() - moment(a.date).unix());
            setUserScans(scans.map((scan, index) => ({ ...scan, id: index })));
        }).then(() => setAreScansLoading(false));

        userNotificationService
            .GetUserNotificationsByUserId(Number(userId))
            .then((userNotifs) => {
                if (!userNotifs) return;
                userNotifs.sort((a, b) => moment(b.date).unix() - moment(a.date).unix());
                setUserNotifications(userNotifs);
            }).then(() => setAreAlertsLoading(false));


        UserService.GetUsersByCompany().then((users) => {
            if (!users) return;
            setManagers(users.filter((user) => (user.role === ERole.Admin || user.role === ERole.Client || user.role === ERole.LimitedClient) && user.id !== Number(userId)));
        });
    }, [userId]);

    useEffect(() => {
        setIsUserChanged(false);
        if (JSON.stringify(cachedUser) !== JSON.stringify(userState))
            setIsUserChanged(true);
    }, [userState]);

    return (
        <Box>
            <Box
                sx={(theme) => ({
                    position: "sticky",
                    top: -16,
                    zIndex: theme.zIndex.appBar,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    p: 1,
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                })}
            >
                <EditOnly>
                    <Button
                        startIcon={<Save />}
                        onClick={() => onUpdateUser()}
                        variant="contained"
                        color="primary"
                        disabled={!isValidForm() || (!isUserChanged && !isSchedulesChanged)}
                    >
                        {t("table.user.edit")}
                    </Button>
                </EditOnly>
            </Box>
            <Box component={Paper} sx={{ width: "100%", position: "relative" }}>
                <FormWrapper>
                    {isErrorMessageVisible && (
                        <Alert
                            severity="error"
                            sx={{ marginBottom: "1em" }}
                        >
                            {t("table.user.editErrorMessage")}
                        </Alert>
                    )}

                    {userState.microsoftId && (<Box
                        sx={{
                            position: "absolute",
                            right: "1em",
                            top: "1em",
                        }}
                    >
                        <Chip
                            size="medium"
                            label={t("table.user.microsoftAccount")}
                            color={"info"}
                            sx={{ fontWeight: theme.typography.fontWeightMedium }}
                        />
                    </Box>)}



                    <UserFormFields
                        userState={userState}
                        setUserState={
                            setUserState as React.Dispatch<
                                React.SetStateAction<
                                    IUserState | IUpdatedUser
                                >
                            >
                        }
                        managers={managers}
                        propertyGroups={propertyGroups}
                    ></UserFormFields>

                    <EntryWrapper>
                        <EntryTitle>{t("table.user.creationDate")} </EntryTitle>
                        <EntryField>
                            <TextField
                                disabled
                                style={{
                                    textAlign: "left",
                                    width: "100%",
                                }}
                                value={moment.utc(userState.createDate).format("LLL")}
                                onChange={(e) =>
                                    updateUserState(
                                        setUserState,
                                        "createDate",
                                        e.target.value
                                    )
                                }
                            />
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper>
                        <EntryTitle>{t("table.user.status")} </EntryTitle>
                        <EntryField>
                            <TextField
                                disabled
                                style={{
                                    textAlign: "left",
                                    width: "100%",
                                }}
                                value={userState.isLocked ? t("table.user.inactive") : t("table.user.active")}
                                onChange={(e) =>
                                    updateUserState(
                                        setUserState,
                                        "isLocked",
                                        e.target.value
                                    )
                                }
                            />
                        </EntryField>
                    </EntryWrapper>

                    <EntryWrapper
                        sx={{
                            justifyContent: "flex-end",
                            marginBlock: "1em",
                        }}
                    >
                        {(cachedUser.role === ERole.Admin || cachedUser.role === ERole.Manager || cachedUser.role === ERole.Supervisor || cachedUser.role === ERole.Client || cachedUser.role === ERole.LimitedClient) && cachedUser.email && !cachedUser.microsoftId && (
                            <EditOnly>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setIsPwdConfirmationOpen(true)}
                                >
                                    {t("table.user.resetPassword")}
                                </Button>
                                <PasswordResetDialog
                                    isOpen={isPwdConfirmationOpen}
                                    onClose={() => setIsPwdConfirmationOpen(false)}
                                    isPwdResetSuccessVisible={isPwdResetSuccessVisible}
                                    isPwdResetErrorVisible={isPwdResetErrorVisible}
                                    onResetPassword={onResetPassword}
                                />
                            </EditOnly>
                        )}

                        {!cachedUser.microsoftId && (
                            <EditOnly>
                                <Button
                                    variant="contained"
                                    color={userState.isLocked ? "success" : "error"}
                                    onClick={() => setIsStatusConfirmationOpen(true)}
                                    sx={{
                                        marginLeft: "1em",
                                    }}
                                >
                                    {userState.isLocked ? t("table.user.activateAccount") : t("table.user.deactivateAccount")}
                                </Button>
                            </EditOnly>
                        )}

                        <UserStatusDialog
                            isOpen={isStatusConfirmationOpen}
                            onClose={() => setIsStatusConfirmationOpen(false)}
                            onToggleUserStatus={onToggleUserStatus}
                            isUserLocked={userState.isLocked}
                        />
                    </EntryWrapper>
                </FormWrapper>
            </Box>

            <Box component={Paper} sx={{ width: "100%", marginTop: 4 }}>
                <WorkSchedulesSection
                    userId={userState.id}
                    propertyGroups={propertyGroups}
                    propertyGroupIds={userState.propertyGroupIds}
                    onSchedulesChange={setWorkSchedules}
                    setInitialSchedules={setInitialSchedules}
                />
            </Box>

            {(userState.role === ERole.Admin || userState.role === ERole.Client) && (
                <Box component={Paper} sx={{ width: "100%", marginTop: 4 }}>
                    <Box sx={{ padding: 2, fontWeight: "bold" }}>
                        {t("table.user.title")}
                    </Box>
                    <DataTable
                        dense
                        disableFiltrer={true}
                        rows={managersUsers}
                        columns={usersColumns}
                        onRowClick={(row) =>
                            navigate(".", { state: { ...row } })
                        }
                        rowsPerPageOptions={[ROWS_PER_PAGE]}
                    />
                </Box>
            )}

            <Box component={Paper} sx={{ width: "100%", marginTop: 4 }}>
                <Box sx={{ padding: 2, fontWeight: "bold" }}>
                    {t("table.scan.title")}
                </Box>
                <DataTable
                    dense
                    disableFiltrer={true}
                    rows={userScans}
                    columns={scansColumns}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    isLoading={areScansLoading}
                />
            </Box>

            <Box component={Paper} sx={{ width: "100%", marginY: 4 }}>
                <Box sx={{ padding: 2, fontWeight: "bold" }}>
                    {t("table.userNotification.title")}
                </Box>
                <DataTable
                    dense
                    disableFiltrer={true}
                    rows={userNotifications}
                    columns={userNotificationsColumns}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    isLoading={areAlertsLoading}
                />
            </Box>
        </Box>
    );
};

export default UserEditPage;
